package com.heelion.farmafreelance.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ExitToApp
import androidx.compose.material.icons.filled.Home
import androidx.compose.material.icons.filled.Person
import androidx.compose.material.icons.filled.Star
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.AccountBox
import androidx.compose.material.icons.filled.Email
import androidx.compose.material.icons.filled.Phone
import androidx.compose.material.icons.filled.Place
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import farmafreelance.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import com.heelion.farmafreelance.ABBONAMENTI_ATTIVI
import com.heelion.farmafreelance.PAYMENT_APP_ID
import com.heelion.farmafreelance.PAYMENT_HUB_URL
import com.heelion.farmafreelance.models.*
import com.heelion.farmafreelance.utils.ItalianCfUtils
import com.heelion.farmafreelance.utils.StimaFiscale
import com.heelion.farmafreelance.utils.TimeUtils

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ProfileScreen(
    viewModel: AuthViewModel,
    currentLanguage: String = "it",
    onLanguageChange: (String) -> Unit = {},
    onLogout: () -> Unit,
    onNavigateToPreferiti: () -> Unit,
    onNavigateToSupporto: () -> Unit,
    onNavigateToStorico: () -> Unit,
    onNavigateToGuadagni: () -> Unit = {},
    onNavigateToPagamenti: () -> Unit = {},
    onNavigateToAgenda: () -> Unit = {}
) {
    val email = viewModel.currentUserEmail
    val role = viewModel.currentUserRole
    val currentUserFarmacista = viewModel.currentUserFarmacista
    val currentUserInfermiere = viewModel.currentUserInfermiere
    val currentUserFarmacia = viewModel.currentUserFarmacia
    // Farmacista o infermiere: i campi in comune si leggono e si mostrano allo stesso modo.
    val professionista = viewModel.currentUserProfessionista
    val isProfessionista = role?.isProfessionista == true
    val isInfermiere = role == UserRole.INFERMIERE
    
    var isEditing by remember { mutableStateOf(false) }
    val scrollState = rememberScrollState()
    
    val themeColorHex = viewModel.selectedThemeColor.hex.removePrefix("0x").toLong(16)
    val themeColor = Color(themeColorHex)

    val sectionTitle = stringResource(Res.string.tab_profile)
    var nome by remember { mutableStateOf("") }
    var cognome by remember { mutableStateOf("") }
    var sesso by remember { mutableStateOf("") }
    var dataNascita by remember { mutableStateOf("") }
    var luogoNascita by remember { mutableStateOf("") }
    var cittadinanza by remember { mutableStateOf("") }
    var cf by remember { mutableStateOf("") }
    var piva by remember { mutableStateOf("") }
    var matricola by remember { mutableStateOf("") }
    var telefono by remember { mutableStateOf("") }
    var titoloStudio by remember { mutableStateOf("") }
    var comuneOperativita by remember { mutableStateOf("") }
    var capOperativita by remember { mutableStateOf("") }
    var indirizzo by remember { mutableStateOf("") }
    var citta by remember { mutableStateOf("") }
    var cap by remember { mutableStateOf("") }
    var provincia by remember { mutableStateOf("") }
    var regione by remember { mutableStateOf("") }
    var tipo by remember { mutableStateOf("") }
    
    // Campi Fiscali
    var regimeFiscale by remember { mutableStateOf("FORFETTARIO") }
    var percEnpaf by remember { mutableStateOf("2.0") }
    var aliquotaForfettario by remember { mutableStateOf(StimaFiscale.ALIQUOTA_ORDINARIA_FORFETTARIO) }
    var ragioneSociale by remember { mutableStateOf("") }
    var codiceSDI by remember { mutableStateOf("") }
    var pec by remember { mutableStateOf("") }
    

    // Solo infermieri: polizza RC professionale
    var polizzaRcNumero by remember { mutableStateOf("") }
    var polizzaRcScadenza by remember { mutableStateOf("") }

    // Competenze
    val competenzePredefinite = competenzeDi(role ?: UserRole.FARMACISTA)
    val selectedSkills = remember { mutableStateListOf<String>() }
    var showOtherSkillField by remember { mutableStateOf(false) }
    var otherSkillText by remember { mutableStateOf("") }
    var expandedCompetenze by remember { mutableStateOf(false) }

    // Stati per i menu a tendina geografici
    var expRegione by remember { mutableStateOf(false) }
    var expProvincia by remember { mutableStateOf(false) }
    var expComune by remember { mutableStateOf(false) }
    var expCap by remember { mutableStateOf(false) }

    // Sincronizza lo stato locale quando i dati dal ViewModel cambiano
    /** Rimette nei campi i dati salvati del professionista (all'apertura e con "Annulla"). */
    fun caricaProfessionista(p: Professionista) {
        nome = p.nome
        cognome = p.cognome
        sesso = p.sesso ?: ""
        dataNascita = p.dataNascita ?: ""
        luogoNascita = p.luogoNascita ?: ""
        cittadinanza = p.cittadinanza ?: ""
        cf = p.codiceFiscale ?: ""
        piva = p.partitaIva
        telefono = p.telefono ?: ""
        titoloStudio = p.titoloStudio ?: ""
        regione = p.regioneOperativita ?: ""
        provincia = p.provinciaOperativita ?: ""
        comuneOperativita = p.comuneOperativita ?: ""
        capOperativita = p.capOperativita ?: ""
        regimeFiscale = p.regimeFiscale ?: "FORFETTARIO"
        aliquotaForfettario = p.aliquotaForfettario
        selectedSkills.clear()
        selectedSkills.addAll(p.competenze)
        when (p) {
            is Farmacista -> {
                matricola = p.numeroIscrizioneOrdine ?: ""
                percEnpaf = p.percEnpaf.toString()
            }
            is Infermiere -> {
                matricola = p.numeroIscrizioneOpi ?: ""
                percEnpaf = p.percEnpapi.toString()
                polizzaRcNumero = p.polizzaRcNumero ?: ""
                polizzaRcScadenza = dataItaliana(p.polizzaRcScadenza)
            }
        }
    }

    LaunchedEffect(currentUserFarmacista, currentUserInfermiere, currentUserFarmacia, role) {
        if (isProfessionista && professionista != null) {
            caricaProfessionista(professionista)
        } else if (role == UserRole.FARMACIA && currentUserFarmacia != null) {
            nome = currentUserFarmacia.nomeFarmacia
            ragioneSociale = currentUserFarmacia.ragioneSociale ?: ""
            piva = currentUserFarmacia.partitaIva
            cf = currentUserFarmacia.codiceFiscale ?: ""
            codiceSDI = currentUserFarmacia.codiceSDI ?: ""
            pec = currentUserFarmacia.pec ?: ""
            matricola = currentUserFarmacia.numeroIscrizioneOrdineDirettore ?: ""
            telefono = currentUserFarmacia.telefono ?: ""
            indirizzo = currentUserFarmacia.indirizzo ?: ""
            citta = currentUserFarmacia.citta ?: ""
            cap = currentUserFarmacia.cap ?: ""
            provincia = currentUserFarmacia.provincia ?: ""
            regione = currentUserFarmacia.regione ?: ""
            tipo = currentUserFarmacia.tipo ?: ""
        }
    }

    var errorMessage by remember { mutableStateOf<String?>(null) }
    var showErrorDialog by remember { mutableStateOf(false) }

    if (showErrorDialog && errorMessage != null) {
        AlertDialog(
            onDismissRequest = { showErrorDialog = false },
            title = { Text(stringResource(Res.string.inconsistent_data)) },
            text = { Text(errorMessage!!) },
            confirmButton = {
                Button(onClick = { showErrorDialog = false }) {
                    Text(stringResource(Res.string.got_it))
                }
            }
        )
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(scrollState),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Spacer(modifier = Modifier.height(16.dp))

        // --- SEZIONE CRUSCOTTO (UNIFORME ALLA HOME) ---
        Card(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            colors = CardDefaults.cardColors(
                containerColor = lerp(Color.White, themeColor, 0.15f)
            ),
            shape = RoundedCornerShape(16.dp),
            border = androidx.compose.foundation.BorderStroke(
                width = 0.5.dp,
                color = if (themeColor == Color.White) Color.LightGray else themeColor.copy(alpha = 0.2f)
            ),
            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
        ) {
            Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
                // BORDO LATERALE A SINISTRA
                Box(
                    modifier = Modifier
                        .width(6.dp)
                        .fillMaxHeight()
                        .background(if (themeColor == Color.White) Color.Gray else themeColor)
                )
                Column(modifier = Modifier.padding(16.dp)) {
                    Text(
                        text = sectionTitle,
                        style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold),
                        color = if (themeColor == Color.White) Color.Black else themeColor
                    )
                }
            }
        }

        Spacer(modifier = Modifier.height(24.dp))

        Column(modifier = Modifier.fillMaxSize().padding(horizontal = 16.dp)) {
            // CARD IDENTITÀ
            Card(
                modifier = Modifier.fillMaxWidth().border(1.dp, Color.LightGray, RoundedCornerShape(12.dp)),
                shape = RoundedCornerShape(12.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                elevation = CardDefaults.cardElevation(defaultElevation = 6.dp)
            ) {
                Column(modifier = Modifier.fillMaxWidth()) {
                    Box(modifier = Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.primary.copy(alpha = 0.1f)).padding(8.dp)) {
                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                            Text(stringResource(Res.string.identity_header), style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
                            Text(stringResource(Res.string.official_profile), style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                        }
                    }

                    // Intestazione: foto, badge abbonamento, nome ed email. I dettagli vanno sotto su
                    // tutta la larghezza: accanto alla foto (≈180dp su un telefono) i campi messi 3-4
                    // per riga finivano schiacciati a larghezza zero e non si vedevano.
                    Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                        Box(modifier = Modifier.size(72.dp).clip(RoundedCornerShape(8.dp)).background(Color(0xFFEEEEEE)).border(1.dp, Color.LightGray, RoundedCornerShape(8.dp)), contentAlignment = Alignment.Center) {
                            Icon(if(role == UserRole.FARMACIA) Icons.Default.Home else Icons.Default.Person, contentDescription = null, modifier = Modifier.size(44.dp), tint = Color.Gray)
                        }
                        Spacer(modifier = Modifier.width(16.dp))
                        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                            // --- BADGE ABBONAMENTO CON REINDIRIZZAMENTO ---
                            val subStatus = viewModel.subscriptionStatus
                            val uriHandler = LocalUriHandler.current
                            if (ABBONAMENTI_ATTIVI) Surface(
                                color = if (subStatus.isPremium) Color(0xFFFFD700).copy(alpha = 0.2f) else Color.Gray.copy(alpha = 0.1f),
                                shape = RoundedCornerShape(4.dp),
                                modifier = Modifier.padding(bottom = 4.dp).clickable {
                                    if (!subStatus.isPremium) {
                                        val userId = viewModel.getUnivocoUtenteId() ?: ""
                                        val userRole = if (role == UserRole.FARMACIA) "FARMACIA" else role?.name ?: "FARMACISTA"
                                        uriHandler.openUri("$PAYMENT_HUB_URL?app=$PAYMENT_APP_ID&role=$userRole&userId=$userId")
                                    }
                                },
                                border = if (subStatus.isPremium) null else androidx.compose.foundation.BorderStroke(0.5.dp, Color.Gray)
                            ) {
                                val label = if (subStatus.isPremium) {
                                    val expiryStr = subStatus.expiryDate?.let { TimeUtils.formatDate(it) } ?: "N/D"
                                    "PREMIUM FINO AL $expiryStr"
                                } else {
                                    "ACCOUNT BASE (CLICCA PER ATTIVARE)"
                                }

                                Text(
                                    text = label,
                                    modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                                    fontSize = 8.sp,
                                    fontWeight = FontWeight.ExtraBold,
                                    color = if (subStatus.isPremium) Color(0xFFB8860B) else Color.Gray
                                )
                            }
                            Text(
                                if (isProfessionista) "$nome $cognome".trim().uppercase() else nome.uppercase(),
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold
                            )
                            Text(email?.lowercase() ?: "N/D", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                        }
                    }

                    Column(modifier = Modifier.padding(horizontal = 16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        if (isProfessionista) {
                            GrigliaInfo(
                                listOfNotNull(
                                    stringResource(Res.string.gender_label_caps) to sesso,
                                    stringResource(Res.string.born_on) to dataNascita,
                                    stringResource(Res.string.citizenship_label_caps) to cittadinanza.uppercase(),
                                    stringResource(Res.string.tax_code_label_caps) to cf.uppercase(),
                                    stringResource(Res.string.degree_label_caps) to titoloStudio.uppercase(),
                                    (if (isInfermiere) "N. OPI" else "N. ORDINE") to matricola,
                                    stringResource(Res.string.operating_city_label_caps) to comuneOperativita.uppercase(),
                                    stringResource(Res.string.operating_cap_label_caps) to capOperativita.uppercase(),
                                    stringResource(Res.string.province_label_caps) to provincia.uppercase(),
                                    "REGIME" to regimeFiscale,
                                    (if (isInfermiere) "ENPAPI" else "ENPAF") to "$percEnpaf%",
                                    if (isInfermiere) "POLIZZA RC" to (polizzaRcNumero.ifBlank { "Da indicare" } +
                                        polizzaRcScadenza.takeIf { it.isNotBlank() }?.let { " (scade il $it)" }.orEmpty()) else null
                                )
                            )
                            if (selectedSkills.isNotEmpty()) {
                                Text("COMPETENZE", style = MaterialTheme.typography.labelSmall, fontSize = 9.sp, color = Color.Gray, fontWeight = FontWeight.Bold)
                                Text(selectedSkills.joinToString(" · "), style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.primary, fontWeight = FontWeight.Bold)
                            }
                        } else {
                            GrigliaInfo(
                                listOfNotNull(
                                    ragioneSociale.takeIf { it.isNotBlank() }?.let { "RAGIONE SOCIALE" to it.uppercase() },
                                    stringResource(Res.string.region_label_caps) to regione.uppercase(),
                                    stringResource(Res.string.vat_label_caps) to piva,
                                    "CODICE SDI" to codiceSDI.ifBlank { "N/D" },
                                    "PEC" to pec.ifBlank { "N/D" },
                                    "MATRICOLA DIR." to matricola
                                )
                            )
                            InfoRow(stringResource(Res.string.address_label_caps), "$indirizzo, $cap $citta ($provincia)")
                        }
                    }
                    Row(modifier = Modifier.fillMaxWidth().padding(16.dp), horizontalArrangement = Arrangement.End, verticalAlignment = Alignment.Bottom) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(stringResource(Res.string.digital_signature), fontSize = 8.sp, color = Color.Gray)
                            Text(nome, fontFamily = FontFamily.Cursive, fontSize = 22.sp, fontWeight = FontWeight.Bold, color = Color.DarkGray)
                            HorizontalDivider(
                                modifier = Modifier.width(120.dp),
                                thickness = 1.dp,
                                color = Color.LightGray
                            )
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(24.dp))

            if (isEditing) {
                Card(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), colors = CardDefaults.cardColors(containerColor = Color.White)) {
                    Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        if (isProfessionista) {
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                OutlinedTextField(value = nome, onValueChange = { nome = it }, label = { Text(stringResource(Res.string.name_label)) }, modifier = Modifier.weight(1f))
                                OutlinedTextField(value = cognome, onValueChange = { cognome = it }, label = { Text(stringResource(Res.string.surname_label)) }, modifier = Modifier.weight(1f))
                            }
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                                Text(stringResource(Res.string.gender_label_caps) + ": ")
                                RadioButton(selected = sesso == "M", onClick = { sesso = "M" })
                                Text("M")
                                RadioButton(selected = sesso == "F", onClick = { sesso = "F" })
                                Text("F")
                            }
                            OutlinedTextField(value = dataNascita, onValueChange = { dataNascita = it }, label = { Text(stringResource(Res.string.birth_date_label)) }, modifier = Modifier.fillMaxWidth())
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                OutlinedTextField(value = luogoNascita, onValueChange = { luogoNascita = it }, label = { Text(stringResource(Res.string.birth_place_label)) }, modifier = Modifier.weight(1f))
                                OutlinedTextField(value = cittadinanza, onValueChange = { cittadinanza = it }, label = { Text(stringResource(Res.string.citizenship_label)) }, modifier = Modifier.weight(1f))
                            }
                            OutlinedTextField(value = cf, onValueChange = { cf = it }, label = { Text(stringResource(Res.string.tax_code_label)) }, modifier = Modifier.fillMaxWidth())
                            OutlinedTextField(value = piva, onValueChange = { piva = it }, label = { Text(stringResource(Res.string.vat_number)) }, modifier = Modifier.fillMaxWidth())
                            OutlinedTextField(value = matricola, onValueChange = { matricola = it }, label = { Text(if (isInfermiere) "N. iscrizione OPI" else stringResource(Res.string.order_registration_label)) }, modifier = Modifier.fillMaxWidth())
                            if (isInfermiere) {
                                // Cambiando la polizza l'account torna in verifica (lo decide il server).
                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    OutlinedTextField(value = polizzaRcNumero, onValueChange = { polizzaRcNumero = it }, label = { Text("N. polizza RC") }, modifier = Modifier.weight(1f), singleLine = true)
                                    OutlinedTextField(value = polizzaRcScadenza, onValueChange = { polizzaRcScadenza = it }, label = { Text("Scadenza (gg/mm/aaaa)") }, modifier = Modifier.weight(1f), singleLine = true)
                                }
                            }

                            // --- DATI FISCALI DEL PROFESSIONISTA ---
                            Text("Dati Fiscali", style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.primary, fontWeight = FontWeight.Bold)
                            
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                EditableDropdownField(
                                    label = "Regime Fiscale",
                                    value = regimeFiscale,
                                    onValueChange = { regimeFiscale = it },
                                    options = listOf("FORFETTARIO", "ORDINARIO", "MINIMI"),
                                    modifier = Modifier.weight(1f)
                                )
                                OutlinedTextField(
                                    value = percEnpaf,
                                    onValueChange = { percEnpaf = it },
                                    label = { Text(if (isInfermiere) "% ENPAPI" else "% ENPAF") },
                                    modifier = Modifier.weight(0.5f),
                                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal)
                                )
                            }
                            // Solo per il forfettario: aliquota usata per la stima delle imposte in "I miei guadagni".
                            if (StimaFiscale.eForfettario(regimeFiscale)) {
                                Text("Aliquota imposta sostitutiva", style = MaterialTheme.typography.bodySmall)
                                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    FilterChip(
                                        selected = aliquotaForfettario == StimaFiscale.ALIQUOTA_ORDINARIA_FORFETTARIO,
                                        onClick = { aliquotaForfettario = StimaFiscale.ALIQUOTA_ORDINARIA_FORFETTARIO },
                                        label = { Text("15%") }
                                    )
                                    FilterChip(
                                        selected = aliquotaForfettario == StimaFiscale.ALIQUOTA_STARTUP_FORFETTARIO,
                                        onClick = { aliquotaForfettario = StimaFiscale.ALIQUOTA_STARTUP_FORFETTARIO },
                                        label = { Text("5% (primi 5 anni)") }
                                    )
                                }
                            }

                            // --- GESTIONE COMPETENZE IN MODIFICA ---
                            Text("Competenze Professionali", style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.primary, fontWeight = FontWeight.Bold)
                            ExposedDropdownMenuBox(
                                expanded = expandedCompetenze,
                                onExpandedChange = { expandedCompetenze = !expandedCompetenze },
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                OutlinedTextField(
                                    value = if (selectedSkills.isEmpty()) "Aggiungi competenze..." else "${selectedSkills.size} selezionate",
                                    onValueChange = {},
                                    readOnly = true,
                                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedCompetenze) },
                                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                                    shape = RoundedCornerShape(12.dp)
                                )
                                ExposedDropdownMenu(expanded = expandedCompetenze, onDismissRequest = { expandedCompetenze = false }) {
                                    competenzePredefinite.forEach { skill ->
                                        val isSelected = selectedSkills.contains(skill)
                                        DropdownMenuItem(
                                            text = {
                                                Row(verticalAlignment = Alignment.CenterVertically) {
                                                    Checkbox(checked = isSelected, onCheckedChange = null)
                                                    Spacer(Modifier.width(8.dp))
                                                    Text(skill)
                                                }
                                            },
                                            onClick = { if (isSelected) selectedSkills.remove(skill) else selectedSkills.add(skill) }
                                        )
                                    }
                                    DropdownMenuItem(
                                        text = { 
                                            Row(verticalAlignment = Alignment.CenterVertically) {
                                                Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                                                Spacer(Modifier.width(8.dp))
                                                Text("Altro...") 
                                            }
                                        },
                                        onClick = { showOtherSkillField = !showOtherSkillField; expandedCompetenze = false }
                                    )
                                }
                            }

                            if (showOtherSkillField) {
                                Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                                    OutlinedTextField(
                                        value = otherSkillText,
                                        onValueChange = { otherSkillText = it },
                                        label = { Text("Nuova competenza") },
                                        modifier = Modifier.weight(1f),
                                        shape = RoundedCornerShape(12.dp)
                                    )
                                    IconButton(onClick = {
                                        if (otherSkillText.isNotBlank()) {
                                            if (!selectedSkills.contains(otherSkillText.trim())) selectedSkills.add(otherSkillText.trim())
                                            otherSkillText = ""; showOtherSkillField = false
                                        }
                                    }) { Icon(Icons.Default.Check, contentDescription = null, tint = MaterialTheme.colorScheme.primary) }
                                }
                            }

                            if (selectedSkills.isNotEmpty()) {
                                @OptIn(ExperimentalLayoutApi::class)
                                FlowRow(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    selectedSkills.forEach { skill ->
                                        InputChip(
                                            selected = true,
                                            onClick = { selectedSkills.remove(skill) },
                                            label = { Text(skill, fontSize = 11.sp) },
                                            trailingIcon = { Icon(Icons.Default.Close, contentDescription = null, modifier = Modifier.size(14.dp)) }
                                        )
                                    }
                                }
                            }

                            Text(
                                text = stringResource(Res.string.operating_area_title),
                                style = MaterialTheme.typography.labelMedium,
                                color = MaterialTheme.colorScheme.primary,
                                fontWeight = FontWeight.Bold
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            // REGIONE
                            ExposedDropdownMenuBox(
                                expanded = expRegione,
                                onExpandedChange = { expRegione = !expRegione },
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                OutlinedTextField(
                                    value = regione,
                                    onValueChange = {},
                                    readOnly = true,
                                    label = { Text(stringResource(Res.string.region_label)) },
                                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expRegione) },
                                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                                    shape = RoundedCornerShape(12.dp)
                                )
                                ExposedDropdownMenu(expanded = expRegione, onDismissRequest = { expRegione = false }) {
                                    viewModel.listaRegioni.forEach { r ->
                                        DropdownMenuItem(
                                            text = { Text(r) },
                                            onClick = {
                                                regione = r
                                                provincia = ""
                                                comuneOperativita = ""
                                                capOperativita = ""
                                                viewModel.updateProvincePerRegione(r)
                                                expRegione = false
                                            }
                                        )
                                    }
                                }
                            }

                            // PROVINCIA E COMUNE
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                ExposedDropdownMenuBox(
                                    expanded = expProvincia,
                                    onExpandedChange = { if (regione.isNotEmpty()) expProvincia = !expProvincia },
                                    modifier = Modifier.weight(1f)
                                ) {
                                    OutlinedTextField(
                                        value = provincia,
                                        onValueChange = {},
                                        readOnly = true,
                                        enabled = regione.isNotEmpty(),
                                        label = { Text(stringResource(Res.string.province_label)) },
                                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expProvincia) },
                                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                                        shape = RoundedCornerShape(12.dp)
                                    )
                                    ExposedDropdownMenu(expanded = expProvincia, onDismissRequest = { expProvincia = false }) {
                                        viewModel.listaProvince.forEach { p ->
                                            DropdownMenuItem(
                                                text = { Text(p) },
                                                onClick = {
                                                    provincia = p
                                                    comuneOperativita = ""
                                                    capOperativita = ""
                                                    viewModel.updateComuniPerProvincia(p)
                                                    expProvincia = false
                                                }
                                            )
                                        }
                                    }
                                }

                                ExposedDropdownMenuBox(
                                    expanded = expComune,
                                    onExpandedChange = { if (provincia.isNotEmpty()) expComune = !expComune },
                                    modifier = Modifier.weight(2f)
                                ) {
                                    OutlinedTextField(
                                        value = comuneOperativita,
                                        onValueChange = {},
                                        readOnly = true,
                                        enabled = provincia.isNotEmpty(),
                                        label = { Text(stringResource(Res.string.city_row)) },
                                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expComune) },
                                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                                        shape = RoundedCornerShape(12.dp)
                                    )
                                    ExposedDropdownMenu(expanded = expComune, onDismissRequest = { expComune = false }) {
                                        viewModel.listaComuni.forEach { c ->
                                            DropdownMenuItem(
                                                text = { Text(c) },
                                                onClick = {
                                                    comuneOperativita = c
                                                    capOperativita = ""
                                                    viewModel.updateCapPerComune(c)
                                                    expComune = false
                                                }
                                            )
                                        }
                                    }
                                }
                            }

                            // CAP
                            ExposedDropdownMenuBox(
                                expanded = expCap,
                                onExpandedChange = { if (comuneOperativita.isNotEmpty()) expCap = !expCap },
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                OutlinedTextField(
                                    value = capOperativita,
                                    onValueChange = {},
                                    readOnly = true,
                                    enabled = comuneOperativita.isNotEmpty(),
                                    label = { Text(stringResource(Res.string.cap_label)) },
                                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expCap) },
                                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                                    shape = RoundedCornerShape(12.dp)
                                )
                                ExposedDropdownMenu(expanded = expCap, onDismissRequest = { expCap = false }) {
                                    viewModel.listaCap.forEach { cp ->
                                        DropdownMenuItem(
                                            text = { Text(cp) },
                                            onClick = {
                                                capOperativita = cp
                                                expCap = false
                                            }
                                        )
                                    }
                                }
                            }
                        } else {
                            OutlinedTextField(value = nome, onValueChange = { nome = it }, label = { Text(stringResource(Res.string.pharmacy_name_label)) }, modifier = Modifier.fillMaxWidth())
                            OutlinedTextField(value = ragioneSociale, onValueChange = { ragioneSociale = it }, label = { Text("Ragione Sociale") }, modifier = Modifier.fillMaxWidth())
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                OutlinedTextField(value = piva, onValueChange = { piva = it }, label = { Text(stringResource(Res.string.vat_number)) }, modifier = Modifier.weight(1f))
                                OutlinedTextField(value = cf, onValueChange = { cf = it }, label = { Text(stringResource(Res.string.tax_code_label)) }, modifier = Modifier.weight(1f))
                            }
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                OutlinedTextField(value = codiceSDI, onValueChange = { codiceSDI = it }, label = { Text("Codice SDI") }, modifier = Modifier.weight(0.5f))
                                OutlinedTextField(value = pec, onValueChange = { pec = it }, label = { Text("PEC") }, modifier = Modifier.weight(1f))
                            }
                            OutlinedTextField(value = matricola, onValueChange = { matricola = it }, label = { Text(stringResource(Res.string.director_registration_label)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
                            OutlinedTextField(value = indirizzo, onValueChange = { indirizzo = it }, label = { Text(stringResource(Res.string.address_label)) }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp))
                            
                            // REGIONE FARMACIA
                            ExposedDropdownMenuBox(
                                expanded = expRegione,
                                onExpandedChange = { expRegione = !expRegione },
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                OutlinedTextField(
                                    value = regione,
                                    onValueChange = {},
                                    readOnly = true,
                                    label = { Text(stringResource(Res.string.region_label)) },
                                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expRegione) },
                                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                                    shape = RoundedCornerShape(12.dp)
                                )
                                ExposedDropdownMenu(expanded = expRegione, onDismissRequest = { expRegione = false }) {
                                    viewModel.listaRegioni.forEach { r ->
                                        DropdownMenuItem(
                                            text = { Text(r) },
                                            onClick = {
                                                regione = r
                                                provincia = ""
                                                citta = ""
                                                cap = ""
                                                viewModel.updateProvincePerRegione(r)
                                                expRegione = false
                                            }
                                        )
                                    }
                                }
                            }

                            // PROVINCIA E CITTA FARMACIA
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                ExposedDropdownMenuBox(
                                    expanded = expProvincia,
                                    onExpandedChange = { if (regione.isNotEmpty()) expProvincia = !expProvincia },
                                    modifier = Modifier.weight(1f)
                                ) {
                                    OutlinedTextField(
                                        value = provincia,
                                        onValueChange = {},
                                        readOnly = true,
                                        enabled = regione.isNotEmpty(),
                                        label = { Text(stringResource(Res.string.province_label)) },
                                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expProvincia) },
                                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                                        shape = RoundedCornerShape(12.dp)
                                    )
                                    ExposedDropdownMenu(expanded = expProvincia, onDismissRequest = { expProvincia = false }) {
                                        viewModel.listaProvince.forEach { p ->
                                            DropdownMenuItem(
                                                text = { Text(p) },
                                                onClick = {
                                                    provincia = p
                                                    citta = ""
                                                    cap = ""
                                                    viewModel.updateComuniPerProvince(listOf(p))
                                                    expProvincia = false
                                                }
                                            )
                                        }
                                    }
                                }

                                ExposedDropdownMenuBox(
                                    expanded = expComune,
                                    onExpandedChange = { if (provincia.isNotEmpty()) expComune = !expComune },
                                    modifier = Modifier.weight(2f)
                                ) {
                                    OutlinedTextField(
                                        value = citta,
                                        onValueChange = {},
                                        readOnly = true,
                                        enabled = provincia.isNotEmpty(),
                                        label = { Text(stringResource(Res.string.city_label)) },
                                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expComune) },
                                        modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                                        shape = RoundedCornerShape(12.dp)
                                    )
                                    ExposedDropdownMenu(expanded = expComune, onDismissRequest = { expComune = false }) {
                                        viewModel.listaComuni.forEach { c ->
                                            DropdownMenuItem(
                                                text = { Text(c) },
                                                onClick = {
                                                    citta = c
                                                    cap = ""
                                                    viewModel.updateCapPerComune(c)
                                                    expComune = false
                                                }
                                            )
                                        }
                                    }
                                }
                            }

                            // CAP FARMACIA
                            ExposedDropdownMenuBox(
                                expanded = expCap,
                                onExpandedChange = { if (citta.isNotEmpty()) expCap = !expCap },
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                OutlinedTextField(
                                    value = cap,
                                    onValueChange = {},
                                    readOnly = true,
                                    enabled = citta.isNotEmpty(),
                                    label = { Text(stringResource(Res.string.cap_label)) },
                                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expCap) },
                                    modifier = Modifier.menuAnchor(MenuAnchorType.PrimaryNotEditable, true).fillMaxWidth(),
                                    shape = RoundedCornerShape(12.dp)
                                )
                                ExposedDropdownMenu(expanded = expCap, onDismissRequest = { expCap = false }) {
                                    viewModel.listaCap.forEach { cp ->
                                        DropdownMenuItem(
                                            text = { Text(cp) },
                                            onClick = {
                                                cap = cp
                                                expCap = false
                                            }
                                        )
                                    }
                                }
                            }
                            OutlinedTextField(value = tipo, onValueChange = { tipo = it }, label = { Text("Tipo (Galenica, ecc. - Facoltativo)") }, modifier = Modifier.fillMaxWidth())
                        }
                        
                        OutlinedTextField(value = telefono, onValueChange = { telefono = it }, label = { Text(stringResource(Res.string.phone_label)) }, modifier = Modifier.fillMaxWidth())

                        if (errorMessage != null) {
                            Text(errorMessage!!, color = Color.Red, style = MaterialTheme.typography.labelSmall)
                        }

                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Button(
                                onClick = { 
                                    errorMessage = null
                                    if (isProfessionista) {
                                        val nomePuro = nome.trim()
                                        val cognomePuro = cognome.trim()
                                        val cfPuro = cf.uppercase().trim()
                                        
                                        val erroreValidazione = ItalianCfUtils.validaTutto(nomePuro, cognomePuro, sesso, dataNascita, cfPuro)
                                        
                                        if (nomePuro.isBlank() || cognomePuro.isBlank() || sesso.isBlank() || dataNascita.isBlank()) {
                                            errorMessage = "Errore: Compila tutti i campi obbligatori!"
                                            showErrorDialog = true
                                        } else if (erroreValidazione != null) {
                                            errorMessage = erroreValidazione
                                            showErrorDialog = true
                                        } else if (isInfermiere) {
                                            viewModel.updateInfermiereProfile(Infermiere(
                                                nome = nomePuro, cognome = cognomePuro, sesso = sesso,
                                                codiceFiscale = cfPuro, dataNascita = dataNascita,
                                                numeroIscrizioneOpi = matricola.trim(),
                                                luogoNascita = luogoNascita, cittadinanza = cittadinanza,
                                                titoloStudio = titoloStudio, email = email ?: "",
                                                partitaIva = piva, telefono = telefono,
                                                regimeFiscale = regimeFiscale,
                                                percEnpapi = percEnpaf.toDoubleOrNull() ?: 4.0,
                                                aliquotaForfettario = aliquotaForfettario,
                                                regioneOperativita = regione,
                                                provinciaOperativita = provincia,
                                                comuneOperativita = comuneOperativita,
                                                capOperativita = capOperativita,
                                                competenze = selectedSkills.toList(),
                                                polizzaRcNumero = polizzaRcNumero.trim().ifBlank { null },
                                                polizzaRcScadenza = dataIso(polizzaRcScadenza)
                                            ))
                                            isEditing = false
                                            errorMessage = null
                                        } else {
                                            viewModel.updateProfile(Farmacista(
                                                nome = nomePuro, cognome = cognomePuro, sesso = sesso,
                                                codiceFiscale = cfPuro, dataNascita = dataNascita,
                                                numeroIscrizioneOrdine = matricola.trim(),
                                                luogoNascita = luogoNascita, cittadinanza = cittadinanza,
                                                titoloStudio = titoloStudio, email = email ?: "", 
                                                partitaIva = piva, telefono = telefono,
                                                regimeFiscale = regimeFiscale,
                                                percEnpaf = percEnpaf.toDoubleOrNull() ?: 2.0,
                                                aliquotaForfettario = aliquotaForfettario,
                                                regioneOperativita = regione,
                                                provinciaOperativita = provincia,
                                                comuneOperativita = comuneOperativita, 
                                                capOperativita = capOperativita,
                                                competenze = selectedSkills.toList()
                                            ))
                                            isEditing = false
                                            errorMessage = null
                                        }
                                    } else {
                                        if (nome.isBlank() || piva.isBlank() || cap.isBlank() || citta.isBlank() || provincia.isBlank()) {
                                            errorMessage = "Dati obbligatori mancanti"
                                        } else {
                                            viewModel.updateFarmaciaProfile(Farmacia(
                                                nomeFarmacia = nome, ragioneSociale = ragioneSociale,
                                                partitaIva = piva, codiceFiscale = cf,
                                                codiceSDI = codiceSDI, pec = pec,
                                                numeroIscrizioneOrdineDirettore = matricola.trim(),
                                                indirizzo = indirizzo, citta = citta, cap = cap,
                                                provincia = provincia, regione = regione,
                                                telefono = telefono, email = email ?: "", tipo = tipo
                                            ))
                                            isEditing = false
                                        }
                                    }
                                },
                                modifier = Modifier.weight(1f)
                            ) { Text(stringResource(Res.string.save_button)) }
                            OutlinedButton(
                                onClick = { 
                                    // RIPRISTINA I DATI ORIGINALI
                                    if (isProfessionista && professionista != null) {
                                        caricaProfessionista(professionista)
                                    } else if (role == UserRole.FARMACIA && currentUserFarmacia != null) {
                                        nome = currentUserFarmacia.nomeFarmacia
                                        piva = currentUserFarmacia.partitaIva
                                        cf = currentUserFarmacia.codiceFiscale ?: ""
                                        telefono = currentUserFarmacia.telefono ?: ""
                                        indirizzo = currentUserFarmacia.indirizzo ?: ""
                                        citta = currentUserFarmacia.citta ?: ""
                                        cap = currentUserFarmacia.cap ?: ""
                                        provincia = currentUserFarmacia.provincia ?: ""
                                        regione = currentUserFarmacia.regione ?: ""
                                        tipo = currentUserFarmacia.tipo ?: ""
                                    }
                                    errorMessage = null
                                    isEditing = false 
                                }, 
                                modifier = Modifier.weight(1f)
                            ) { Text(stringResource(Res.string.cancel_button)) }
                        }
                    }
                }
            } else {
                Button(onClick = { isEditing = true }, modifier = Modifier.fillMaxWidth()) { 
                    Text(if(role == UserRole.FARMACIA) stringResource(Res.string.edit_data_pharmacy) else stringResource(Res.string.edit_data_professional)) 
                }
                
                Spacer(modifier = Modifier.height(12.dp))
                
                OutlinedButton(
                    onClick = onNavigateToPreferiti,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Icon(AppIcons.Favorite, contentDescription = null, tint = Color.Red)
                    Spacer(Modifier.width(8.dp))
                    Text(stringResource(Res.string.my_favorites))
                }

                Spacer(modifier = Modifier.height(12.dp))

                OutlinedButton(
                    onClick = onNavigateToStorico,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Icon(Icons.Default.Check, contentDescription = null)
                    Spacer(Modifier.width(8.dp))
                    Text(stringResource(Res.string.storico_title))
                }

                Spacer(modifier = Modifier.height(12.dp))

                // Turni da fare (confermati o in attesa) e turni svolti, in un'unica agenda.
                OutlinedButton(onClick = onNavigateToAgenda, modifier = Modifier.fillMaxWidth()) {
                    Icon(AppIcons.Event, contentDescription = null)
                    Spacer(Modifier.width(8.dp))
                    Text("Agenda turni")
                }

                Spacer(modifier = Modifier.height(12.dp))

                // Solo farmacia: la parte infermieri compare solo se la farmacia lavora anche con loro.
                if (role == UserRole.FARMACIA) {
                    val infermiere = com.heelion.farmafreelance.models.Professione.INFERMIERE
                    OutlinedCard(modifier = Modifier.fillMaxWidth()) {
                        Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                            Icon(infermiere.icona, contentDescription = null, tint = infermiere.colore, modifier = Modifier.size(24.dp))
                            Spacer(Modifier.width(10.dp))
                            Column(modifier = Modifier.weight(1f)) {
                                Text("Servizi infermieristici", fontWeight = FontWeight.Bold)
                                Text(
                                    if (viewModel.infermieriAttivi) "Trovi farmacisti e infermieri separati con il selettore in alto."
                                    else "Prelievi, iniezioni, medicazioni...: attivalo per trovare anche infermieri.",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = Color.Gray
                                )
                            }
                            Switch(
                                checked = viewModel.infermieriAttivi,
                                onCheckedChange = { viewModel.impostaServiziInfermieristici(it) }
                            )
                        }
                    }
                    Spacer(modifier = Modifier.height(12.dp))
                }

                // Solo per i professionisti: lordo e cassa (ENPAF/ENPAPI) stimati mese per mese.
                if (isProfessionista) {
                    OutlinedButton(onClick = onNavigateToGuadagni, modifier = Modifier.fillMaxWidth()) {
                        Text("€", fontWeight = FontWeight.Bold)
                        Spacer(Modifier.width(8.dp))
                        Text("I miei guadagni")
                    }
                    Spacer(modifier = Modifier.height(12.dp))
                    // Agenda personale: per ogni farmacia e mese, spuntare quando si è stati pagati.
                    OutlinedButton(onClick = onNavigateToPagamenti, modifier = Modifier.fillMaxWidth()) {
                        Icon(AppIcons.Check, contentDescription = null)
                        Spacer(Modifier.width(8.dp))
                        Text("Resoconto pagamenti")
                    }
                    Spacer(modifier = Modifier.height(12.dp))
                }

                OutlinedButton(
                    onClick = onNavigateToSupporto,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Icon(Icons.Default.Star, contentDescription = null) // Placeholder, use appropriate icon
                    Spacer(Modifier.width(8.dp))
                    Text(stringResource(Res.string.support_feedback))
                }
            }
        }
    }
}

@Composable
fun InfoRow(label: String, value: String, modifier: Modifier = Modifier) {
    Column(modifier) {
        Text(label, style = MaterialTheme.typography.labelSmall, fontSize = 9.sp, color = Color.Gray, fontWeight = FontWeight.Bold)
        Text(value.ifBlank { "—" }, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.SemiBold, color = Color.Black)
    }
}

/** Dati del profilo 2 per riga, ognuno metà della larghezza: leggibili anche su un telefono. */
@Composable
private fun GrigliaInfo(campi: List<Pair<String, String>>) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        campi.chunked(2).forEach { riga ->
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                riga.forEach { (etichetta, valore) -> InfoRow(etichetta, valore, Modifier.weight(1f)) }
                if (riga.size == 1) Spacer(modifier = Modifier.weight(1f))
            }
        }
    }
}
